import type { ConstellationFigure, StarCatalogue } from '@/lib/astro/catalogue'

import { bvToRgb } from '@/lib/astro/sky'
import { ALGOL, COMPARISONS } from '@/lib/heroes/algol'

/*
 * A 2D canvas renderer for the Research hero: the camera on the 40 cm reflector, centred on Algol.
 *
 * The sky is drawn in a gnomonic (camera) projection about Algol from the real catalogue, filled in with
 * fainter field stars once the view narrows. The intro pushes in from all of Perseus to the telescope's field,
 * a brass reticle closes and locks on, and the photometry apertures appear around Algol and its two
 * comparison stars while Algol itself dims through the eclipse.
 */

const DEG = Math.PI / 180
const TAU = Math.PI * 2
/** Only stars within this angle of Algol can ever be in frame. */
const FIELD_COS = Math.cos(44 * DEG)
/** Stars brighter than this are drawn every frame, so they can scintillate; the rest are cached. */
const BRIGHT = 4.2
const BRASS = '198, 167, 110'
const STAR = '237, 230, 216'
const ALGOL_RGB = '226, 236, 255'

const clamp01 = (value: number): number => (value < 0 ? 0 : value > 1 ? 1 : value)

const smoothstep = (edge0: number, edge1: number, value: number): number => {
    const t = clamp01((value - edge0) / (edge1 - edge0))

    return t * t * (3 - 2 * t)
}

const easeOutBack = (t: number): number => {
    const c1 = 1.4
    const c3 = c1 + 1

    return 1 + c3 * (t - 1) ** 3 + c1 * (t - 1) ** 2
}

const mulberry32 = (seed: number): (() => number) => {
    let state = seed >>> 0

    return () => {
        state = (state + 0x6d2b79f5) >>> 0

        let t = state

        t = Math.imul(t ^ (t >>> 15), t | 1)
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61)

        return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }
}

/** The page's own typefaces, read from the variables _app.tsx writes, with system fallbacks. */
const fontStack = (variable: string, fallback: string): string => {
    try {
        const value = getComputedStyle(document.documentElement).getPropertyValue(variable).trim()

        return value ? `${value}, ${fallback}` : fallback
    }
    catch {
        return fallback
    }
}

type FieldStar = {
    xi: number
    eta: number
    mag: number
    colour: string
    phase: number
    /** How strongly a faint field star shows once the view is narrow; 0 for catalogue stars. */
    deep: number
}

type Comparison = {
    xi: number
    eta: number
    label: string
}

export type EclipseFrame = {
    /** Wall-clock seconds, for scintillation and the turning scale. */
    seconds: number
    /** Field of view across the longer side of the frame, in degrees. */
    fov: number
    /** How far the reticle has closed and locked on, 0 to 1. */
    lock: number
    /** How visible the photometry apertures are, 0 to 1. */
    photometry: number
    /** Algol's magnitude in this frame. */
    magnitude: number
    /** A brief brightening of the aperture each time a frame is read out, 0 to 1. */
    pulse: number
}

export type EclipseFieldOptions = {
    phone: boolean
}

export class EclipseField {
    private readonly canvas: HTMLCanvasElement
    private readonly ctx: CanvasRenderingContext2D
    private readonly phone: boolean
    private readonly ra0 = ALGOL.ra * 15 * DEG
    private readonly sinDec0 = Math.sin(ALGOL.dec * DEG)
    private readonly cosDec0 = Math.cos(ALGOL.dec * DEG)
    private width = 0
    private height = 0
    private dpr = 1
    private cx = 0
    private cy = 0
    private scale = 1
    private stars: FieldStar[] = []
    private readonly deepStars: FieldStar[]
    private comparisons: Comparison[] = []
    private figure: { lines: [number, number][][], anchor: [number, number] | null } | null = null
    private readonly sprite: HTMLCanvasElement
    private readonly grainTile: HTMLCanvasElement
    private readonly cache: HTMLCanvasElement
    private readonly cacheCtx: CanvasRenderingContext2D | null
    private cacheKey = ''
    private vignette: CanvasGradient | null = null
    private readonly mono: string
    private readonly serif: string

    constructor(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, options: EclipseFieldOptions) {
        this.canvas = canvas
        this.ctx = ctx
        this.phone = options.phone
        this.sprite = this.createSprite()
        this.grainTile = this.createGrain()
        this.cache = document.createElement('canvas')
        this.cacheCtx = this.cache.getContext('2d', { alpha: false })
        this.deepStars = this.createDeepStars(options.phone ? 900 : 1400)
        this.mono = fontStack('--font-app-mono', 'ui-monospace, Menlo, Consolas, monospace')
        this.serif = fontStack('--font-app-heading', 'Georgia, "Times New Roman", serif')
    }

    setCatalogue(catalogue: StarCatalogue): void {
        const stars: FieldStar[] = []
        const random = mulberry32(14576)
        const comparisons: Comparison[] = []

        for (let i = 0; i < catalogue.count; i++) {
            if (catalogue.hip[i] === ALGOL.hip) continue

            const projected = this.tangent(catalogue.ra[i] * DEG, catalogue.dec[i] * DEG)

            if (!projected) continue

            const r = Math.round(Math.min(1, catalogue.rgb[i * 3] * 1.08) * 255)
            const g = Math.round(Math.min(1, catalogue.rgb[i * 3 + 1] * 1.08) * 255)
            const b = Math.round(Math.min(1, catalogue.rgb[i * 3 + 2] * 1.08) * 255)

            stars.push({
                xi: projected[0],
                eta: projected[1],
                mag: catalogue.mag[i],
                colour: `rgb(${r}, ${g}, ${b})`,
                phase: random(),
                deep: 0
            })

            const comparison = COMPARISONS.find(item => item.hip === catalogue.hip[i])

            if (comparison) comparisons.push({ xi: projected[0], eta: projected[1], label: `${comparison.label}  ${comparison.name}  ${comparison.mag.toFixed(2)}` })
        }

        this.stars = stars
        this.comparisons = comparisons
        this.cacheKey = ''
    }

    setFigures(figures: ConstellationFigure[]): void {
        const perseus = figures.find(figure => figure.id === 'Per')

        if (!perseus) return

        const toTangent = ([x, y, z]: [number, number, number]): [number, number] | null => {
            const ra = Math.atan2(y, x)
            const dec = Math.asin(Math.max(-1, Math.min(1, z)))

            return this.tangent(ra, dec)
        }

        const lines = perseus.lines
            .map(line => line.map(toTangent).filter((point): point is [number, number] => point !== null))
            .filter(line => line.length > 1)

        this.figure = { lines, anchor: toTangent(perseus.anchor) }
    }

    resize(width: number, height: number, dpr: number): void {
        this.width = width
        this.height = height
        this.dpr = dpr
        this.canvas.width = Math.max(1, Math.round(width * dpr))
        this.canvas.height = Math.max(1, Math.round(height * dpr))
        this.cache.width = this.canvas.width
        this.cache.height = this.canvas.height
        this.cacheKey = ''

        const portrait = height > width

        this.cx = width * 0.5
        this.cy = height * (portrait ? 0.27 : 0.4)

        const ctx = this.ctx
        const vignette = ctx.createRadialGradient(this.cx, this.cy, Math.min(width, height) * 0.25, this.cx, this.cy, Math.hypot(width, height) * 0.7)

        vignette.addColorStop(0, 'rgba(0, 0, 0, 0)')
        vignette.addColorStop(1, 'rgba(0, 0, 0, 0.62)')
        this.vignette = vignette
    }

    /** Draws one frame of the camera view. */
    render(frame: EclipseFrame): void {
        const { ctx, width, height, dpr } = this

        if (!width || !height) return

        this.scale = (Math.max(width, height) / 2) / Math.tan((frame.fov / 2) * DEG)

        const zoom = smoothstep(46, 10, frame.fov)

        this.renderCache(frame.fov, zoom)

        ctx.setTransform(1, 0, 0, 1, 0, 0)
        ctx.globalCompositeOperation = 'source-over'
        ctx.globalAlpha = 1
        ctx.drawImage(this.cache, 0, 0)
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

        this.drawFigure(smoothstep(30, 44, frame.fov))
        this.drawBrightStars(frame.seconds, zoom)
        this.drawAlgol(frame, zoom)
        this.drawComparisons(frame.photometry)
        this.drawReticle(frame)

        if (this.vignette) {
            ctx.globalCompositeOperation = 'source-over'
            ctx.globalAlpha = 1
            ctx.fillStyle = this.vignette
            ctx.fillRect(0, 0, width, height)
        }
    }

    /** Right ascension and declination under a point on the canvas. */
    pick(x: number, y: number): { ra: number, dec: number } | null {
        if (!this.width || x < 0 || y < 0 || x > this.width || y > this.height) return null

        const xi = (this.cx - x) / this.scale
        const eta = (this.cy - y) / this.scale
        const rho = Math.hypot(xi, eta)

        if (rho === 0) return { ra: ALGOL.ra, dec: ALGOL.dec }

        const c = Math.atan(rho)
        const sinC = Math.sin(c)
        const cosC = Math.cos(c)
        const dec = Math.asin(cosC * this.sinDec0 + (eta * sinC * this.cosDec0) / rho)
        const ra = this.ra0 + Math.atan2(xi * sinC, rho * this.cosDec0 * cosC - eta * this.sinDec0 * sinC)
        let hours = (ra / DEG / 15) % 24

        if (hours < 0) hours += 24

        return { ra: hours, dec: dec / DEG }
    }

    /** Gnomonic projection about Algol, in radians on the tangent plane, east to the left. */
    private tangent(ra: number, dec: number): [number, number] | null {
        const sinDec = Math.sin(dec)
        const cosDec = Math.cos(dec)
        const deltaRa = ra - this.ra0
        const cosC = this.sinDec0 * sinDec + this.cosDec0 * cosDec * Math.cos(deltaRa)

        if (cosC < FIELD_COS) return null

        return [
            (cosDec * Math.sin(deltaRa)) / cosC,
            (this.cosDec0 * sinDec - this.sinDec0 * cosDec * Math.cos(deltaRa)) / cosC
        ]
    }

    private x(xi: number): number {
        return this.cx - xi * this.scale
    }

    private y(eta: number): number {
        return this.cy - eta * this.scale
    }

    /** The background, the faint stars and the grain, redrawn only while the view is still zooming. */
    private renderCache(fov: number, zoom: number): void {
        const ctx = this.cacheCtx

        if (!ctx) return

        const key = `${fov.toFixed(3)}|${this.stars.length}|${this.width}x${this.height}`

        if (key === this.cacheKey) return

        this.cacheKey = key

        const { width, height } = this

        ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0)
        ctx.globalCompositeOperation = 'source-over'
        ctx.globalAlpha = 1

        const sky = ctx.createLinearGradient(0, 0, width, height)

        sky.addColorStop(0, '#020309')
        sky.addColorStop(0.55, '#040811')
        sky.addColorStop(1, '#070c18')
        ctx.fillStyle = sky
        ctx.fillRect(0, 0, width, height)

        // The edge of the Milky Way runs through Perseus: a faint band across the frame.
        const band = ctx.createLinearGradient(width * 0.1, height, width * 0.9, 0)

        band.addColorStop(0, 'rgba(60, 74, 112, 0)')
        band.addColorStop(0.45, 'rgba(60, 74, 112, 0.07)')
        band.addColorStop(0.55, 'rgba(84, 92, 126, 0.1)')
        band.addColorStop(1, 'rgba(60, 74, 112, 0)')
        ctx.fillStyle = band
        ctx.fillRect(0, 0, width, height)

        const deepAlpha = smoothstep(24, 11, fov)

        ctx.globalCompositeOperation = 'lighter'

        if (deepAlpha > 0.01) {
            for (const star of this.deepStars) this.drawStar(ctx, star, deepAlpha * star.deep * 1.6, zoom)
        }

        for (const star of this.stars) {
            if (star.mag >= BRIGHT) this.drawStar(ctx, star, 1, zoom)
        }

        ctx.globalCompositeOperation = 'source-over'
        ctx.globalAlpha = 1

        const grain = ctx.createPattern(this.grainTile, 'repeat')

        if (grain) {
            if (typeof grain.setTransform === 'function' && typeof DOMMatrix !== 'undefined') {
                grain.setTransform(new DOMMatrix().scaleSelf(1 / this.dpr, 1 / this.dpr))
            }

            ctx.fillStyle = grain
            ctx.fillRect(0, 0, width, height)
        }
    }

    private drawStar(ctx: CanvasRenderingContext2D, star: FieldStar, alpha: number, zoom: number): void {
        const x = this.x(star.xi)
        const y = this.y(star.eta)

        if (x < -30 || y < -30 || x > this.width + 30 || y > this.height + 30) return

        const size = Math.max(0.42, (6.9 - star.mag) * 0.34) * (0.8 + zoom * 0.55)
        const light = Math.min(1, Math.max(0.07, 1 - (star.mag - 1.2) * 0.12)) * alpha

        ctx.globalAlpha = light
        ctx.fillStyle = star.colour
        ctx.beginPath()
        ctx.arc(x, y, size, 0, TAU)
        ctx.fill()

        if (star.mag < 3.8) {
            const glow = size * (6 + zoom * 4)

            ctx.globalAlpha = light * 0.38
            ctx.drawImage(this.sprite, x - glow, y - glow, glow * 2, glow * 2)
        }
    }

    private drawBrightStars(seconds: number, zoom: number): void {
        const { ctx } = this

        ctx.globalCompositeOperation = 'lighter'

        for (const star of this.stars) {
            if (star.mag >= BRIGHT) continue

            const twinkle = 0.84 + 0.16 * Math.sin(seconds * (1.4 + star.phase * 1.1) + star.phase * 9)

            this.drawStar(ctx, star, twinkle, zoom)
        }

        ctx.globalCompositeOperation = 'source-over'
        ctx.globalAlpha = 1
    }

    private drawFigure(alpha: number): void {
        const { ctx, figure } = this

        if (!figure || alpha < 0.01) return

        ctx.globalAlpha = alpha * 0.4
        ctx.strokeStyle = `rgb(${BRASS})`
        ctx.lineWidth = 0.8
        ctx.setLineDash([])
        ctx.beginPath()

        for (const line of figure.lines) {
            line.forEach(([xi, eta], index) => {
                if (index === 0) ctx.moveTo(this.x(xi), this.y(eta))
                else ctx.lineTo(this.x(xi), this.y(eta))
            })
        }

        ctx.stroke()

        if (figure.anchor) {
            ctx.globalAlpha = alpha * 0.7
            ctx.fillStyle = `rgb(${BRASS})`
            ctx.font = `italic 300 ${this.phone ? 18 : 24}px ${this.serif}`
            ctx.textAlign = 'center'
            ctx.fillText('Perseus', this.x(figure.anchor[0]), this.y(figure.anchor[1]))
        }

        ctx.globalAlpha = 1
    }

    /** Algol itself, its light following the eclipse, with the four diffraction spikes of the reflector's spider vanes. */
    private drawAlgol(frame: EclipseFrame, zoom: number): void {
        const { ctx, cx, cy } = this
        const flux = 10 ** (-0.4 * (frame.magnitude - ALGOL.mag))
        const seeing = 1 + 0.045 * Math.sin(frame.seconds * 7.3) + 0.03 * Math.sin(frame.seconds * 12.7 + 1.1)
        const core = (1.5 + 2.4 * Math.sqrt(flux)) * (0.85 + zoom * 0.6)

        ctx.globalCompositeOperation = 'lighter'

        const halo = core * 26 * Math.sqrt(flux)

        ctx.globalAlpha = 0.13 * flux
        ctx.drawImage(this.sprite, cx - halo, cy - halo, halo * 2, halo * 2)

        const glow = core * 9 * seeing

        ctx.globalAlpha = 0.6 * flux
        ctx.drawImage(this.sprite, cx - glow, cy - glow, glow * 2, glow * 2)

        const spike = (34 + 110 * zoom) * flux * seeing

        if (spike > 4) {
            ctx.lineWidth = 1.1

            for (let i = 0; i < 4; i++) {
                const angle = (i * Math.PI) / 2
                const ex = cx + Math.cos(angle) * spike
                const ey = cy + Math.sin(angle) * spike
                const gradient = ctx.createLinearGradient(cx, cy, ex, ey)

                gradient.addColorStop(0, `rgba(${ALGOL_RGB}, ${0.75 * flux})`)
                gradient.addColorStop(1, `rgba(${ALGOL_RGB}, 0)`)
                ctx.globalAlpha = 1
                ctx.strokeStyle = gradient
                ctx.beginPath()
                ctx.moveTo(cx, cy)
                ctx.lineTo(ex, ey)
                ctx.stroke()
            }
        }

        ctx.globalCompositeOperation = 'source-over'
        ctx.globalAlpha = 1
        ctx.fillStyle = `rgb(${ALGOL_RGB})`
        ctx.beginPath()
        ctx.arc(cx, cy, core, 0, TAU)
        ctx.fill()
    }

    private reticleRadius(): number {
        return Math.min(this.width, this.height) * (this.phone ? 0.13 : 0.12)
    }

    private drawComparisons(alpha: number): void {
        const { ctx } = this

        if (alpha < 0.01 || !this.comparisons.length) return

        const radius = this.reticleRadius() * 0.24

        ctx.font = `400 ${this.phone ? 9 : 10}px ${this.mono}`
        ctx.textAlign = 'left'
        ctx.textBaseline = 'middle'
        this.setSpacing('1.4px')

        for (const comparison of this.comparisons) {
            const x = this.x(comparison.xi)
            const y = this.y(comparison.eta)

            if (x < 0 || y < 0 || x > this.width || y > this.height) continue

            ctx.globalAlpha = alpha * 0.7
            ctx.strokeStyle = `rgb(${BRASS})`
            ctx.lineWidth = 0.8
            ctx.setLineDash([2, 3])
            ctx.beginPath()
            ctx.arc(x, y, radius, 0, TAU)
            ctx.stroke()
            ctx.setLineDash([])

            // The label sits to the right of the aperture, or to the left when it would run off the frame.
            const label = comparison.label.toUpperCase()
            const fitsRight = x + radius + 8 + ctx.measureText(label).width < this.width - 12

            ctx.globalAlpha = alpha * 0.75
            ctx.fillStyle = `rgb(${STAR})`
            ctx.textAlign = fitsRight ? 'left' : 'right'
            ctx.fillText(label, fitsRight ? x + radius + 8 : x - radius - 8, y)
        }

        this.setSpacing('0px')
        ctx.textBaseline = 'alphabetic'
        ctx.globalAlpha = 1
    }

    /** The graduated reticle that closes in and locks on, and the photometry aperture and sky annulus inside it. */
    private drawReticle(frame: EclipseFrame): void {
        const { ctx, cx, cy } = this

        if (frame.lock <= 0.001) return

        const base = this.reticleRadius()
        const closed = easeOutBack(clamp01(frame.lock))
        const radius = base * (1 + 2.6 * (1 - closed))
        const alpha = smoothstep(0, 0.35, frame.lock)
        const turn = frame.seconds * 0.025 + (1 - closed) * 1.4

        ctx.strokeStyle = `rgb(${BRASS})`
        ctx.setLineDash([])

        // The graduated ring.
        ctx.globalAlpha = alpha * 0.55
        ctx.lineWidth = 0.8
        ctx.beginPath()
        ctx.arc(cx, cy, radius, 0, TAU)
        ctx.stroke()

        ctx.beginPath()

        for (let degree = 0; degree < 360; degree += 5) {
            const major = degree % 30 === 0
            const angle = degree * DEG + turn
            const inner = radius - (major ? 9 : 4)
            const cos = Math.cos(angle)
            const sin = Math.sin(angle)

            ctx.moveTo(cx + cos * inner, cy + sin * inner)
            ctx.lineTo(cx + cos * radius, cy + sin * radius)
        }

        ctx.globalAlpha = alpha * 0.7
        ctx.stroke()

        // The crosshair, with a gap in the middle so it never covers the star.
        ctx.globalAlpha = alpha * 0.75
        ctx.lineWidth = 0.9
        ctx.beginPath()

        for (let i = 0; i < 4; i++) {
            const angle = (i * Math.PI) / 2 + Math.PI / 4
            const cos = Math.cos(angle)
            const sin = Math.sin(angle)

            ctx.moveTo(cx + cos * radius * 0.7, cy + sin * radius * 0.7)
            ctx.lineTo(cx + cos * radius * 1.12, cy + sin * radius * 1.12)
        }

        ctx.stroke()

        // The photometry aperture and the sky annulus that measures the background.
        const photometry = frame.photometry

        if (photometry > 0.01) {
            ctx.globalAlpha = photometry * (0.72 + frame.pulse * 0.28)
            ctx.lineWidth = 0.9 + frame.pulse * 0.8
            ctx.beginPath()
            ctx.arc(cx, cy, base * 0.3, 0, TAU)
            ctx.stroke()

            ctx.globalAlpha = photometry * 0.42
            ctx.lineWidth = 0.7
            ctx.setLineDash([3, 4])
            ctx.beginPath()
            ctx.arc(cx, cy, base * 0.46, 0, TAU)
            ctx.stroke()
            ctx.beginPath()
            ctx.arc(cx, cy, base * 0.6, 0, TAU)
            ctx.stroke()
            ctx.setLineDash([])

            // The readout flash: a ring that expands from the aperture as each frame is read.
            if (frame.pulse > 0.01) {
                ctx.globalAlpha = photometry * frame.pulse * 0.45
                ctx.lineWidth = 0.8
                ctx.beginPath()
                ctx.arc(cx, cy, base * (0.3 + (1 - frame.pulse) * 0.4), 0, TAU)
                ctx.stroke()
            }

            // The target's label, up and to the left of the ring.
            const labelX = cx - radius * 0.78
            const labelY = cy - radius * 0.86

            ctx.font = `400 ${this.phone ? 9 : 10}px ${this.mono}`
            ctx.textAlign = 'right'
            this.setSpacing('1.6px')
            ctx.globalAlpha = photometry
            ctx.fillStyle = `rgb(${BRASS})`
            ctx.fillText('ALGOL  ·  β PER', labelX, labelY)
            ctx.globalAlpha = photometry * 0.8
            ctx.fillStyle = `rgb(${STAR})`
            ctx.fillText(`V ${frame.magnitude.toFixed(2)}`, labelX, labelY + 15)
            this.setSpacing('0px')
        }

        ctx.globalAlpha = 1
    }

    private setSpacing(value: string): void {
        const ctx = this.ctx as CanvasRenderingContext2D & { letterSpacing?: string }

        if ('letterSpacing' in ctx) ctx.letterSpacing = value
    }

    /** Faint stars below the catalogue's limit, scattered through the telescope's field and thinning out at its edge. */
    private createDeepStars(count: number): FieldStar[] {
        const random = mulberry32(4113)
        const stars: FieldStar[] = []
        const spread = 0.13

        for (let i = 0; i < count; i++) {
            const bv = -0.1 + random() * 1.5
            const [r, g, b] = bvToRgb(bv, 0.6)
            const distance = spread * Math.sqrt(random())
            const angle = random() * TAU

            stars.push({
                xi: Math.cos(angle) * distance,
                eta: Math.sin(angle) * distance,
                mag: 6 + 3.6 * Math.sqrt(random()),
                colour: `rgb(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)})`,
                phase: random(),
                deep: smoothstep(spread, spread * 0.7, distance)
            })
        }

        return stars
    }

    private createSprite(): HTMLCanvasElement {
        const size = 64
        const sprite = document.createElement('canvas')
        const context = sprite.getContext('2d')

        sprite.width = size
        sprite.height = size

        if (!context) return sprite

        const gradient = context.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2)

        gradient.addColorStop(0, 'rgba(255, 255, 255, 1)')
        gradient.addColorStop(0.18, 'rgba(255, 255, 255, 0.45)')
        gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.08)')
        gradient.addColorStop(1, 'rgba(255, 255, 255, 0)')
        context.fillStyle = gradient
        context.fillRect(0, 0, size, size)

        return sprite
    }

    private createGrain(): HTMLCanvasElement {
        const size = 128
        const tile = document.createElement('canvas')
        const context = tile.getContext('2d')

        tile.width = size
        tile.height = size

        if (!context) return tile

        const image = context.createImageData(size, size)
        const random = mulberry32(1807)

        for (let i = 0; i < size * size; i++) {
            const value = random()
            const light = value > 0.5
            const strength = Math.abs(value - 0.5) * 2
            const shade = light ? 255 : 0

            image.data[i * 4] = shade
            image.data[i * 4 + 1] = shade
            image.data[i * 4 + 2] = shade
            image.data[i * 4 + 3] = Math.round(strength * strength * (light ? 18 : 26))
        }

        context.putImageData(image, 0, 0)

        return tile
    }
}
